import { FieldArray, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type Form2848Data,
  type Form2848Matter,
  MAX_MATTERS,
  RETURN_OWNERSHIP_OPTIONS,
} from "@/features/forms/types";
import { FormikCell, FormikSelect } from "./formik-controls";

const EMPTY_MATTER: Form2848Matter = {
  tax_type: "",
  description: "",
  tax_form: "",
  years: "",
  return_ownership: "",
};

/**
 * "Tax Matters" — the Description / Tax Form / Year(s) rows that fill the 2848
 * Part I table (capped at the template's three rows), plus a Type of
 * Return/Ownership selection stored alongside each row.
 */
export function TaxMattersTable() {
  const { values } = useFormikContext<Form2848Data>();
  const matters = values.matters;

  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description of Matter</TableHead>
                  <TableHead className="w-36">Tax Form Number</TableHead>
                  <TableHead className="w-44">Year(s) or Period(s)</TableHead>
                  <TableHead className="w-52">Type of Return/Ownership</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.description`}
                        placeholder="e.g. Income Tax"
                        aria-label={`Description for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_form`}
                        placeholder="e.g. 1040"
                        aria-label={`Tax form for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Years for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikSelect
                        name={`matters.${i}.return_ownership`}
                        options={RETURN_OWNERSHIP_OPTIONS}
                        placeholder="Select…"
                        aria-label={`Type of return for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form 2848 fits up to {MAX_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}
